import {
    FollowupTaskContext,
    initialFollowupTaskContext,
    TaskEvent,
    Tasks,
} from "@/Contexts/FollowupTaskContext";
import { FollowupTask } from "@/types/followupTask";
import { Client, PageProps } from "@/types/page";
import { onUserEvent } from "@/utils/EventListener";
import { usePage } from "@inertiajs/react";
import axios from "axios";
import {
    differenceInMilliseconds,
    isAfter,
    isFuture,
    subMinutes,
} from "date-fns";
import { PropsWithChildren, useEffect, useState } from "react";

export const NUM_MINUTES_UNTIL_COMPLETED_TASK_AUTO_CLEAR = 60;

export const FollowupTaskProvider = ({ children }: PropsWithChildren) => {
    const { user, twoFactorPassed } = usePage<PageProps>().props;

    const [allTasks, setAllTasks] = useState(
        initialFollowupTaskContext.allTasks,
    );
    const [_, setLastRender] = useState(Date.now());

    const shownTasks = Object.values(allTasks)
        .flat()
        .filter((task) => task.show_at && !isFuture(task.show_at))
        .filter(
            (task) =>
                !task.completed_at ||
                isAfter(
                    task.completed_at,
                    subMinutes(
                        new Date(),
                        NUM_MINUTES_UNTIL_COMPLETED_TASK_AUTO_CLEAR,
                    ),
                ),
        )
        .reduce((acc, task) => {
            if (!acc[task.client_id]) acc[task.client_id] = [];
            acc[task.client_id].push(task);
            return acc;
        }, {} as Tasks);

    const shownTasksWithoutSnoozed = Object.values(allTasks)
        .flat()
        .filter((task) => task.show_at && !isFuture(task.show_at))
        .filter((task) => !task.completed_at)
        .filter((task) => !task.snoozed_at || !isFuture(task.snoozed_at))
        .reduce((acc, task) => {
            if (!acc[task.client_id]) acc[task.client_id] = [];
            acc[task.client_id].push(task);
            return acc;
        }, {} as Tasks);

    const shownTasksEligibleForDraft = Object.values(shownTasksWithoutSnoozed)
        .flat()
        .filter((task) => task.is_considered_in_draft)
        .reduce((acc, task) => {
            if (!acc[task.client_id]) acc[task.client_id] = [];
            acc[task.client_id].push(task);
            return acc;
        }, {} as Tasks);

    const numberOfClientsWithTasks = Object.values(
        shownTasksWithoutSnoozed,
    ).reduce((acc, tasks) => acc + (tasks.length > 0 ? 1 : 0), 0);

    const timeUntilNextReevaluation = Math.min(
        ...Object.values<FollowupTask[]>(allTasks)
            .flat()
            .filter(
                (task) => task.reevaluate_at && isFuture(task.reevaluate_at),
            )
            .map((task) =>
                differenceInMilliseconds(
                    task.reevaluate_at ?? Infinity,
                    new Date(),
                ),
            ),
    );

    const timeUntilNextShowOrSnooze = Math.min(
        ...Object.values<FollowupTask[]>(allTasks)
            .flat()
            .map((task) => {
                const timesUntilEvent: number[] = [];
                if (task.show_at && isFuture(task.show_at)) {
                    timesUntilEvent.push(
                        differenceInMilliseconds(
                            task.show_at ?? Infinity,
                            new Date(),
                        ),
                    );
                }
                if (task.snoozed_at && isFuture(task.snoozed_at)) {
                    timesUntilEvent.push(
                        differenceInMilliseconds(
                            task.snoozed_at ?? Infinity,
                            new Date(),
                        ),
                    );
                }
                return timesUntilEvent;
            })
            .flat(),
    );

    onUserEvent<TaskEvent>("FollowupTaskUpdatedBroadcast", (e) => {
        const updatedTask = e.followupTask;
        setAllTasks((prevTasks) => {
            const clientOldTasks = prevTasks[updatedTask.client_id] ?? [];
            const taskIndex = clientOldTasks.findIndex(
                (t) => t.id === updatedTask.id,
            );
            if (taskIndex === -1) {
                // The task is not in the list, so we add it
                return {
                    ...prevTasks,
                    [updatedTask.client_id]: [...clientOldTasks, updatedTask],
                };
            }
            const newClientOldTasks = [...clientOldTasks];
            newClientOldTasks[taskIndex] = updatedTask;
            return {
                ...prevTasks,
                [updatedTask.client_id]: newClientOldTasks,
            };
        });
    });

    onUserEvent<TaskEvent>("FollowupTaskCreatedBroadcast", (e) => {
        const newTask = e.followupTask;
        if (newTask.completed_at) return;
        setAllTasks((prevTasks) => {
            const clientOldTasks = prevTasks[newTask.client_id] ?? [];
            if (clientOldTasks.find((t) => t.id === newTask.id))
                return prevTasks;
            return {
                ...prevTasks,
                [newTask.client_id]: [...clientOldTasks, newTask],
            };
        });
    });

    onUserEvent<TaskEvent>("FollowupTaskRemovedBroadcast", (e) => {
        const removedTask = e.followupTask;
        setAllTasks((prevTasks) => {
            const clientOldTasks = prevTasks[removedTask.client_id] ?? [];
            return {
                ...prevTasks,
                [removedTask.client_id]: clientOldTasks.filter(
                    (t) => t.id !== removedTask.id,
                ),
            };
        });
    });

    const getClientTasks = (client_id: Client["id"]) => {
        return shownTasks[client_id] ?? [];
    };

    const getClientTasksWithoutSnoozed = (client_id: Client["id"]) => {
        return shownTasksWithoutSnoozed[client_id] ?? [];
    };

    const deleteTask = (task: FollowupTask) => {
        setAllTasks((prevTasks) => ({
            ...prevTasks,
            [task.client_id]: (prevTasks[task.client_id] ?? []).filter(
                (t) => t.id !== task.id,
            ),
        }));
    };

    const markTaskAsDoneVisually = async (task: FollowupTask) => {
        setAllTasks((prevTasks) => ({
            ...prevTasks,
            [task.client_id]: (prevTasks[task.client_id] ?? []).map((t) =>
                t.id === task.id
                    ? { ...t, completed_at: new Date().toISOString() }
                    : t,
            ),
        }));
    };

    const undoTaskCompletionVisually = async (task: FollowupTask) => {
        setAllTasks((prevTasks) => ({
            ...prevTasks,
            [task.client_id]: (prevTasks[task.client_id] ?? []).map((t) =>
                t.id === task.id ? { ...t, completed_at: null } : t,
            ),
        }));
    };

    const loadTasks = async () => {
        const { data } = await axios.get<Tasks | undefined>(
            route("followup-tasks"),
        );
        setAllTasks(data ?? {});
    };

    useEffect(() => {
        if (user?.type !== "coach" || !twoFactorPassed) {
            setAllTasks({});
        } else {
            loadTasks();
        }
    }, [user?.type, user?.id, twoFactorPassed]);

    useEffect(() => {
        if (user?.type !== "coach" || !twoFactorPassed) return;
        let reevaluationTimeout = -1;
        if (
            timeUntilNextReevaluation > 0 &&
            timeUntilNextReevaluation !== Infinity
        ) {
            reevaluationTimeout = window.setTimeout(
                () => loadTasks(),
                timeUntilNextReevaluation,
            );
        }
        let showAtTimeout = -1;
        if (
            timeUntilNextShowOrSnooze > 0 &&
            timeUntilNextShowOrSnooze !== Infinity
        ) {
            showAtTimeout = window.setTimeout(
                () => setLastRender(Date.now()),
                timeUntilNextShowOrSnooze,
            );
        }
        const refreshRegularlyTimeout = window.setTimeout(
            () => loadTasks(),
            1_000 * 60 * 10,
        );
        return () => {
            window.clearTimeout(reevaluationTimeout);
            window.clearTimeout(showAtTimeout);
            window.clearTimeout(refreshRegularlyTimeout);
        };
    }, [
        timeUntilNextReevaluation,
        timeUntilNextShowOrSnooze,
        user?.type,
        user?.id,
        twoFactorPassed,
    ]);

    return (
        <FollowupTaskContext.Provider
            value={{
                allTasks,
                shownTasks,
                shownTasksWithoutSnoozed,
                shownTasksEligibleForDraft,
                numberOfClientsWithTasks,
                getClientTasks,
                getClientTasksWithoutSnoozed,
                deleteTask,
                markTaskAsDoneVisually,
                undoTaskCompletionVisually,
            }}
        >
            {children}
        </FollowupTaskContext.Provider>
    );
};
